import React, { useState, useEffect } from 'react'
import { Modal } from '../UI/Modal/Modal'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { SamplesApi } from '../../services/samplesApi'
import { toast } from 'react-toastify'
import { handleFormError } from '../../utils/handleFormError'
import { OrderDetailsStep } from './FormSteps/OrderDetailsStep'
import { OrderInfoStep } from './FormSteps/OrderInfoStep'
import { ProductSelectionStep } from './FormSteps/ProductSelectionStep'
import { PreviewStep } from './FormSteps/PreviewStep'
import { SampleRequestCreateUpdate } from '../../models/samples'
import { useQueryClient } from '@tanstack/react-query'

interface SampleRequestFormModalProps {
  show: boolean
  onHide: () => void
  sampleRequestId?: number | null
  initialCompanyId?: number
  initialDoorProxyId?: number
}

const sampleRequestSchema = z
  .object({
    contact_company_id: z.number().optional(),
    chain_id: z.number().optional().nullable(),
    door_proxy_id: z.number().optional().nullable(),
    date_needed: z.string().min(1, 'Date needed is required'),
    fulfillment_option: z.string().min(1, 'Fulfillment option is required'),
    distributor_id: z.number().optional().nullable(),
    contact_name: z.string().min(1, 'Contact name is required'),
    contact_email: z
      .string()
      .email('Valid email is required')
      .min(1, 'Contact email is required'),
    contact_phone: z.string().optional(),
    shipping_address_line1: z.string().min(1, 'Address is required'),
    shipping_address_line2: z.string().optional(),
    shipping_city: z.string().min(1, 'City is required'),
    shipping_state: z.string().min(1, 'State is required'),
    shipping_zip: z
      .string()
      .min(1, 'ZIP code is required')
      .max(5, 'ZIP code must be 5 digits')
      .regex(/^\d{5}$/, 'ZIP code must be 5 digits'),
    shipping_country: z.string().optional(),
    special_instructions: z.string().optional(),
    line_items: z
      .array(
        z.object({
          variant_id: z.number().min(1, 'Product is required'),
          quantity: z.number().min(1, 'Quantity must be at least 1'),
          product_format: z.string().optional(),
        })
      )
      .min(1, 'At least one product is required'),
  })
  .superRefine((data, ctx) => {
    // Either contact_company_id or chain_id must be provided
    // Note: contact_company_id can be 0 if chain_id is provided
    const hasCompanyId =
      data.contact_company_id !== undefined &&
      data.contact_company_id !== null &&
      data.contact_company_id > 0
    const hasChainId = data.chain_id !== undefined && data.chain_id !== null

    if (!hasCompanyId && !hasChainId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Company is required',
        path: ['contact_company_id'],
      })
    }
  })

export function SampleRequestFormModal({
  show,
  onHide,
  sampleRequestId,
  initialCompanyId,
  initialDoorProxyId,
}: SampleRequestFormModalProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const totalSteps = 4
  const queryClient = useQueryClient()

  const formMethods = useForm({
    defaultValues: {
      contact_company_id: initialCompanyId || 0,
      chain_id: null as number | null,
      door_proxy_id: initialDoorProxyId || null,
      date_needed: '',
      fulfillment_option: '',
      distributor_id: null as number | null,
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      shipping_address_line1: '',
      shipping_address_line2: '',
      shipping_city: '',
      shipping_state: '',
      shipping_zip: '',
      shipping_country: 'US',
      special_instructions: '',
      line_items: [] as Array<{
        variant_id: number
        quantity: number
        product_format?: string
      }>,
    },
    mode: 'onChange',
    resolver: zodResolver(sampleRequestSchema),
    reValidateMode: 'onChange',
  })

  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
    setError,
    reset,
    trigger,
  } = formMethods

  // Load existing order data if editing
  useEffect(() => {
    if (sampleRequestId) {
      setIsLoading(true)
      SamplesApi.getSampleRequest(sampleRequestId)
        .then((order) => {
          reset({
            contact_company_id: order.company_id,
            date_needed: order.date_needed,
            fulfillment_option: order.fulfillment_option,
            distributor_id: order.distributor || null,
            contact_name: order.contact_name || '',
            contact_email: order.contact_email || '',
            contact_phone: order.contact_phone || '',
            shipping_address_line1: order.shipping_address_line1 || '',
            shipping_address_line2: order.shipping_address_line2 || '',
            shipping_city: order.shipping_city || '',
            shipping_state: order.shipping_state || '',
            shipping_zip: order.shipping_zip || '',
            shipping_country: order.shipping_country || 'US',
            special_instructions: order.special_instructions || '',
            line_items: order.line_items.map((item) => ({
              variant_id: item.variant,
              quantity: item.quantity || 0,
              product_format: item.product_format || undefined,
            })),
          })
          trigger()
        })
        .catch((err) => {
          console.error('Error loading sample request:', err)
          toast.error('Failed to load sample request')
          onHide()
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [sampleRequestId, reset, trigger, onHide])

  // Set initial company/door when provided (for creating from company page)
  useEffect(() => {
    if (!sampleRequestId && show && (initialCompanyId || initialDoorProxyId)) {
      reset({
        contact_company_id: initialCompanyId || 0,
        chain_id: null,
        door_proxy_id: initialDoorProxyId || null,
        date_needed: '',
        fulfillment_option: '',
        distributor_id: null,
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        shipping_address_line1: '',
        shipping_address_line2: '',
        shipping_city: '',
        shipping_state: '',
        shipping_zip: '',
        shipping_country: 'US',
        special_instructions: '',
        line_items: [],
      })
      // Trigger validation for the pre-filled fields
      if (initialCompanyId) {
        trigger('contact_company_id')
      }
    }
  }, [
    show,
    sampleRequestId,
    initialCompanyId,
    initialDoorProxyId,
    reset,
    trigger,
  ])

  const handleClose = () => {
    reset()
    setCurrentStep(1)
    onHide()
  }

  const onSubmit = handleSubmit(async (values) => {
    try {
      const payload: SampleRequestCreateUpdate = {
        contact_company_id: values.contact_company_id,
        date_needed: values.date_needed,
        fulfillment_option: values.fulfillment_option as any,
        contact_name: values.contact_name,
        contact_email: values.contact_email,
        contact_phone: values.contact_phone,
        shipping_address_line1: values.shipping_address_line1,
        shipping_address_line2: values.shipping_address_line2,
        shipping_city: values.shipping_city,
        shipping_state: values.shipping_state,
        shipping_zip: values.shipping_zip,
        shipping_country: values.shipping_country,
        line_items: values.line_items,
        ...(values.chain_id && { chain_id: values.chain_id }),
        ...(values.door_proxy_id && { door_proxy_id: values.door_proxy_id }),
        ...(values.distributor_id && { distributor_id: values.distributor_id }),
        ...(values.special_instructions && {
          special_instructions: values.special_instructions,
        }),
      }

      if (sampleRequestId) {
        await SamplesApi.updateSampleRequest(sampleRequestId, payload)
        toast.success('Sample request updated successfully')
      } else {
        await SamplesApi.createSampleRequest(payload)
        toast.success('Sample request created successfully')
      }

      // Invalidate and refetch samples queries to update the table
      void queryClient.invalidateQueries({
        queryKey: ['samples-table'],
        exact: false,
      })

      handleClose()
    } catch (err: any) {
      console.error('Error submitting sample request:', err)
      // Navigate to appropriate step based on error
      const errorKeys = Object.keys(err)
      if (
        errorKeys.some(
          (key) =>
            [
              'contact_company_id',
              'contact_name',
              'contact_email',
              'contact_phone',
            ].includes(key) || key.startsWith('shipping_')
        )
      ) {
        setCurrentStep(1)
      } else if (
        errorKeys.some((key) =>
          ['date_needed', 'distributor_id', 'fulfillment_option'].includes(key)
        )
      ) {
        setCurrentStep(2)
      } else if (errorKeys.some((key) => key === 'line_items')) {
        setCurrentStep(3)
      }
      handleFormError(err, setError)
    }
  })

  const handleNext = async () => {
    // Validate current step fields before proceeding
    let fieldsToValidate: string[] = []

    if (currentStep === 1) {
      // Step 1: Company, Contact, Shipping
      fieldsToValidate = [
        'contact_company_id',
        'chain_id', // Include chain_id validation
        'contact_name',
        'contact_email',
        'contact_phone',
        'shipping_address_line1',
        'shipping_city',
        'shipping_state',
        'shipping_zip',
      ]
    } else if (currentStep === 2) {
      // Step 2: Order Information
      fieldsToValidate = ['date_needed', 'fulfillment_option']
    } else if (currentStep === 3) {
      // Step 3: Product Selection
      fieldsToValidate = ['line_items']
    }

    const isStepValid = await trigger(fieldsToValidate as any)

    if (isStepValid) {
      setCurrentStep((s) => s + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((s) => s - 1)
    } else {
      handleClose()
    }
  }

  return (
    <Modal
      open={show}
      onOpenChange={(open) => !open && handleClose()}
      title={sampleRequestId ? 'Edit Sample Request' : 'New Sample Request'}
      description="Create or edit a sample request"
      loading={isSubmitting || isLoading}
      onAccept={currentStep === totalSteps ? onSubmit : handleNext}
      onCancel={handleBack}
      preventCloseOnCancel={true}
      acceptButtonText={
        currentStep === totalSteps
          ? sampleRequestId
            ? 'Update'
            : 'Submit'
          : 'Next'
      }
      cancelButtonText={currentStep > 1 ? 'Back' : 'Cancel'}
      cancelButtonProps={{
        type: 'button',
      }}
      size="lg"
      blockAccept={!isValid && currentStep === totalSteps}
    >
      <FormProvider {...formMethods}>
        {currentStep === 1 && <OrderDetailsStep />}
        {currentStep === 2 && <OrderInfoStep />}
        {currentStep === 3 && <ProductSelectionStep />}
        {currentStep === 4 && <PreviewStep />}
      </FormProvider>
    </Modal>
  )
}
